import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { eventBus, EventTypes } from '@/lib/event-bus';
import { Modal } from '../antd-wrapper/modal/global/modal';
import { IconWrapper } from '../ui/icon-wrapper';
import UploadIcon from '/public/file/upload.svg';
import { Button } from '@/components/ui/button';
import UploadFileList from './uploadFileList';
import {
  UploadContextConfig,
  UploadContextProvider,
  useUpload,
} from '@/contexts/upload-context';
import UploadDesc from './upload-desc';
import {
  useSkillAsyncValid,
  ValidationQueueItem,
} from '@/hooks/deep-mining/use-skills-Async-Vaild';
import ExtendInput from './extend-input';
import { skillService } from '@/services/skill-service';
import { globalModal } from '../antd-wrapper/modal/global';
import { useI18n } from '@/hooks/use-i18n';

const RUNNING_STATUS = ['pending', 'uploading', 'validating'];
const SKILLS_ACCEPT_FILL_TYPE = '.zip,.skill';

export type FileStatus =
  | FILE.UploadStatus
  | 'validating'
  | 'validating-error'
  | 'finished';

export type FileItem = Omit<FILE.UploadItem, 'status'> & {
  status: FileStatus;
  skills_id?: string;
  key?: string;
};

function UploadSkillModalContent({
  validQueue,
  onDeleteValid,
  onRetryValid,
  onClearValid,
}: {
  validQueue: ValidationQueueItem[];
  onDeleteValid: (skills_id: string) => void;
  onRetryValid: (key: string) => void;
  onClearValid: () => void;
}) {
  const { t } = useI18n(['skill', 'common']);
  const [isShow, setIsShow] = useState(false);

  useEffect(() => {
    const unsubscribe = eventBus.on(EventTypes.OPEN_UPLOAD_SKILLS_MODAL, () => {
      setIsShow(true);
    });
    return () => {
      unsubscribe();
    };
  }, []);

  const importSkills = async () => {
    try {
      const skillsIds = showFiles
        ?.map((file) => {
          if (file.status === 'finished') {
            return file.skills_id;
          }
        })
        ?.filter(Boolean);
      await skillService.importSkills({ skills_ids: skillsIds });
      eventBus.emit(EventTypes.SKILLS_IMPORT_SUCCESS);
    } catch (error) {
      console.error(error);
    }
  };

  const {
    files,
    onUploadSelect,
    onUploadDrop,
    onDelete,
    onUploadRetry,
    onClear,
  } = useUpload();

  // 根据validQueue更新showFiles, 对status进行映射更新, 对skills_id进行更新
  const showFiles = useMemo(() => {
    const validQueueMap = new Map<string, ValidationQueueItem>();
    validQueue.forEach((item) => {
      validQueueMap.set(item.key, item);
    });

    return files.map((file) => {
      const validInfo = validQueueMap.get(file.key);
      if (!validInfo) {
        return file as FileItem;
      }

      // 根据validInfo更新file.status,不同的校验阶段对应不同的展示节点
      let newStatus: FileStatus = file.status as FileStatus;
      if (validInfo.status === 'queue' || validInfo.status === 'running') {
        newStatus = 'validating';
      } else if (validInfo.status === 'success') {
        newStatus = 'finished';
      } else if (validInfo.status === 'failed') {
        newStatus = 'validating-error';
      }

      return {
        ...file,
        status: newStatus,
        skills_id: validInfo.skills_id,
      };
    });
  }, [files, validQueue]);

  const onHandleDelete = (file: FileItem) => {
    onDeleteValid(file.skills_id);
    onDelete(file.id);
  };

  const onHandleRetry = (file: FileItem) => {
    // 根据file.status进行不同的重试处理，校验重试和上传重试
    if ((file?.status as FileStatus) === 'validating-error') {
      onRetryValid(file.key);
      return;
    } else if ((file?.status as FileStatus) === 'error') {
      onUploadRetry(file.id);
    }
  };

  // 清空所有上传列表&校验列表
  useLayoutEffect(() => {
    if (!isShow) {
      onClear();
      onClearValid();
    }
  }, [isShow]);

  // 根据showFiles计算导入按钮是否禁用
  const importSkillsDisabled = useMemo(() => {
    const hasSuccessSkills = showFiles?.some(
      (file) => file.status === 'finished',
    );
    const hasRunningSkills = showFiles?.some((file) =>
      RUNNING_STATUS.includes(file.status),
    );
    // 导入按钮可点击的条件： 有成功上传的文件 && 不能有正在运行的文件
    const canImport = hasSuccessSkills && !hasRunningSkills;
    return !canImport;
  }, [showFiles]);

  const onCloseModal = () => {
    // 如果全部都是失败的文件，直接关闭
    const onlyErrorFile = showFiles?.every(
      (file) => file.status === 'validating-error' || file.status === 'error',
    );
    if (onlyErrorFile) {
      setIsShow(false);
      return;
    }

    globalModal({
      key: 'upload-skill-modal',
      title: t('skill:upload.closeConfirm'),
      content: t('skill:upload.closeConfirmContent'),
      okText: t('common:confirm'),
      cancelText: t('common:cancel'),
      onOk: () => {
        setIsShow(false);
      },
    });
  };

  const hasFile = showFiles?.length > 0;

  return (
    <Modal
      headerHeight={64}
      open={isShow}
      closable={false}
      onCancel={onCloseModal}
      title={t('skill:upload.title')}
      styles={{
        footer: {
          margin: 0,
        },
      }}
      width={800}
      height={560}
      footer={null}
      classNames={{ content: '!pb-0' }}
    >
      <div className="px-6 flex flex-col h-full">
        <UploadDesc />
        {!hasFile && (
          <div className="bg-[#FAFAFA] w-full flex-1 flex flex-col items-center justify-center rounded-[8px] my-4">
            <ExtendInput
              onHandleSelect={onUploadSelect}
              onHandleDrop={onUploadDrop}
              multiple
              accept={SKILLS_ACCEPT_FILL_TYPE}
              className="w-full h-full"
              dropEleClassName="w-full h-full flex justify-center items-center"
            >
              <div className="w-full flex-1 flex flex-col items-center justify-center gap-2">
                <IconWrapper icon={UploadIcon} size={32} />
                <span className="text-[#4B5563] text-[16px] font-semibold">
                  {t('skill:upload.uploadTip')}
                </span>
              </div>
            </ExtendInput>
          </div>
        )}
        {hasFile && (
          <div className="flex-1 overflow-y-auto pt-4 flex flex-col">
            <UploadFileList
              files={showFiles}
              onDelete={onHandleDelete}
              onRetry={onHandleRetry}
            />
          </div>
        )}
        <div className="flex items-center h-20 gap-4 text-[13px] font-medium">
          <div className="flex-1">
            {hasFile && (
              <ExtendInput
                onHandleSelect={onUploadSelect}
                multiple
                accept={SKILLS_ACCEPT_FILL_TYPE}
              >
                <Button className="px-4 h-10 bg-[#7171EE] text-white border-none cursor-pointer">
                  {t('common:continueSelect')}
                </Button>
              </ExtendInput>
            )}
          </div>
          <Button
            variant="outline"
            onClick={onCloseModal}
            className="px-4 w-20 h-10 bg-[#7171EE1A] text-[#7171EE] border-none hover:bg-[#7171EE1A] hover:text-[#7171EE] cursor-pointer"
          >
            {t('common:cancel')}
          </Button>
          <Button
            onClick={() => {
              importSkills().then(() => {
                setIsShow(false);
              });
            }}
            disabled={importSkillsDisabled}
            className="px-4 w-20 h-10 bg-[#7171EE] text-white border-none cursor-pointer"
          >
            {t('common:import')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function UploadSkillModal() {
  const { queue, onAddToQueue, onDelete, onRetry, onClear } =
    useSkillAsyncValid();

  const uploadConfig: UploadContextConfig = {
    uploadType: 'skills',
    maxCount: Infinity,
    maxSize: Infinity,
    accept: SKILLS_ACCEPT_FILL_TYPE,
    concurrency: 4,
    onItemSuccess: (item) => {
      onAddToQueue([item.key]);
    },
  };

  return (
    <UploadContextProvider config={uploadConfig}>
      <UploadSkillModalContent
        onDeleteValid={onDelete}
        onRetryValid={onRetry}
        validQueue={queue}
        onClearValid={onClear}
      />
    </UploadContextProvider>
  );
}

export default UploadSkillModal;
